import { FC, useEffect } from 'react';
import { Modal, Form, Input, Button, Typography, Alert } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';

const { Text } = Typography;

/**
 * Props for the LinkAccountModal component.
 */
interface LinkAccountModalProps {
  /** Whether the modal is visible. */
  open: boolean;
  /** Callback function when the modal is cancelled. */
  onCancel: () => void;
  /** The email address that is already linked to an account. */
  linkedEmail: string;
  /** Callback function when the link account form is submitted. */
  onFormSubmit: (values: { email: string; password: string }) => void;
  /** Whether the form submission is in progress. */
  isLoading: boolean;
  /** Error message to display. */
  error?: string | null;
}

/**
 * A modal component that allows users to link their existing account
 * when a social sign-in email matches an existing email/password account.
 */
const LinkAccountModal: FC<LinkAccountModalProps> = ({
  open,
  onCancel,
  linkedEmail,
  onFormSubmit,
  isLoading,
  error,
}) => {
  const [form] = Form.useForm();

  // Reset form fields when the modal is closed or linkedEmail changes
  useEffect(() => {
    if (open) {
      form.setFieldsValue({ email: linkedEmail, password: '' });
    }
  }, [open, linkedEmail, form]);

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };
  
  const handleFormSubmit = (values: { email: string; password: string }) => {
    onFormSubmit(values);
  };

  return (
    <Modal
      title="Link Your Accounts"
      open={open}
      onCancel={handleCancel}
      footer={null}
      destroyOnHidden
    >
      <Text style={{ display: 'block', marginBottom: 20 }}>
        An account already exists with the email {linkedEmail}. Please sign in
        with your credentials to link the accounts.
      </Text>

      <Form
        form={form}
        name="link_account_modal_form"
        onFinish={handleFormSubmit}
        initialValues={{ email: linkedEmail }}
        layout="vertical"
      >
        <Form.Item
          name="email"
          rules={[{ required: true, message: 'Please input your Email!' }]}
        >
          <Input prefix={<UserOutlined />} placeholder="Email" disabled />
        </Form.Item>
        <Form.Item
          name="password"
          label="Password for your existing account"
          rules={[{ required: true, message: 'Please input your Password!' }]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder="Password"
          />
        </Form.Item>
        {error && (
          <Form.Item>
            <Alert title={error} type="error" />
          </Form.Item>
        )}
        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
          <Button
            onClick={handleCancel}
            style={{ marginRight: 10 }}
            disabled={isLoading}
            aria-label="Cancel account linking"
          >
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={isLoading}
            disabled={isLoading}
            aria-label="Confirm account linking"
          >
            Link Accounts
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default LinkAccountModal; 